import { useState } from "react";

import { useToast } from "@aviary";
import type { MerchantDataErrors } from "@shared/data/mutations/MerchantDataMutation/MerchantData.mutation";
import { useMerchantDataMutation } from "@shared/data/mutations/MerchantDataMutation/MerchantData.mutation";
import { useStoreProfitMarginUpdateMutation } from "@shared/data/mutations/StoreProfitMarginUpdateMutation/StoreProfitMarginUpdate.mutation";
import type { MerchantDataSplinter } from "@shared/data/queries/PayoutDetailsQuery/PayoutDetails.query";
import { MarginType } from "@shared/types/marginTypes";

import { OAuthStepHeader } from "../../OAuthStepHeader";
import { OAuthStep } from "../../oauthStep";

import type { Fieldset, FormErrors, FormValues } from "./OAuthBankingForm";
import { OAuthBankingForm } from "./OAuthBankingForm";

import * as styles from "./OAuthBankingInformation.styles";

const formatBirthDate = ({ year, month, day }) => {
  if (year && month && day) {
    const formattedMonth = month.padStart(2, "0");
    const formattedDay = day.padStart(2, "0");

    return `${year}-${formattedMonth}-${formattedDay}`;
  }

  return "";
};

interface Props {
  marginType: string;
  profitMarginIncrements: number[];
  merchantData: MerchantDataSplinter;
  onOnboardingComplete: () => void;
  isFullSsnRequired: boolean;
  onUpdateCurrentStep: (step: OAuthStep) => void;
  onUpdateStoreCurrentSetupPage: (step: string, forProfit?: boolean) => Promise<void>;
}

const OAuthBankingInformation = ({
  marginType,
  profitMarginIncrements,
  merchantData,
  onOnboardingComplete,
  isFullSsnRequired,
  onUpdateCurrentStep,
  onUpdateStoreCurrentSetupPage,
}: Props) => {
  const { makeToast } = useToast();
  const [formErrors, setFormErrors] = useState<FormErrors>(null);
  const [storeProfitMarginUpdate, { loading: storeProfitMarginUpdateLoding }] =
    useStoreProfitMarginUpdateMutation();
  const [saveMerchantData, { loading: saveMerchantDataLoading }] = useMerchantDataMutation();

  const isUpdating = storeProfitMarginUpdateLoding || saveMerchantDataLoading;

  const isNeverMarginStore = marginType === MarginType.NEVER;

  const [values, setValues] = useState<FormValues>({
    marginPercentage: "",
    addressInfo: {
      streetAddress: merchantData?.streetAddress,
      streetAddress2: merchantData?.streetAddress2,
      city: merchantData?.city,
      region: merchantData?.region,
      postalCode: merchantData?.postalCode,
      phone: merchantData?.phone,
    },
    personalInfo: {
      firstName: merchantData?.firstName ?? "",
      lastName: merchantData?.lastName ?? "",
      dateOfBirth: formatBirthDate({
        year: merchantData?.birthYear,
        month: merchantData?.birthMonth,
        day: merchantData?.birthDay,
      }),
      ssn: isNeverMarginStore ? "" : "****",
    },
    businessInfo: {
      accountType: merchantData?.accountType === "personal" ? null : merchantData?.accountType,
      businessName: merchantData?.businessName,
      employerId: merchantData?.employerId,
      classification: merchantData?.classification,
    },
    bankAccountInfo: {
      routingNumber: null,
      accountNumber: null,
      accountHolder: null,
      accountHeldBy: "individual",
      accountType: "checking",
    },
  });

  const onChange = (fieldset: Fieldset) => {
    return newValues => {
      setValues({
        ...values,
        [fieldset]: newValues,
      });
    };
  };

  const handleGoBack = () => {
    onUpdateCurrentStep(OAuthStep.ACCOUNT_TYPE);
  };

  const setFieldsErrors = (merchantDataErrors: MerchantDataErrors) => {
    const errorFields = merchantDataErrors.fields;
    setFormErrors({
      businessInfo: {
        accountType: errorFields?.accountType,
        businessName: errorFields?.businessName,
        employerId: errorFields?.employerId,
        classification: errorFields?.classification,
      },
      personalInfo: {
        firstName: errorFields?.firstName,
        lastName: errorFields?.lastName,
        dateOfBirth: errorFields?.dateOfBirth,
        ssn: errorFields?.ssn,
      },
      addressInfo: {
        streetAddress: errorFields?.streetAddress,
        city: errorFields?.city,
        region: errorFields?.region,
        postalCode: errorFields?.postalCode,
        phone: errorFields?.phone,
      },
      bankAccountInfo: {
        routingNumber: errorFields?.bankRoutingNumber,
        accountNumber: errorFields?.bankAccountNumber,
        accountHolder: errorFields?.bankAccountHolder,
        accountHeldBy: errorFields?.bankAccountHeldBy,
        accountType: errorFields?.bankAccountType,
      },
    });
  };

  const handleComplete = async () => {
    const { data: storeProfitMarginUpdateData } = await storeProfitMarginUpdate({
      variables: { input: { attributes: { profitMargin: parseInt(values.marginPercentage, 10) } } },
    });

    if (storeProfitMarginUpdateData?.errors?.message) {
      makeToast("error", storeProfitMarginUpdateData.errors.message);
    } else {
      const { data } = await saveMerchantData({
        variables: {
          input: {
            attributes: {
              ...values.businessInfo,
              ...values.addressInfo,
              ...values.personalInfo,
              bankRoutingNumber: values.bankAccountInfo.routingNumber,
              bankAccountNumber: values.bankAccountInfo.accountNumber,
              bankAccountType: values.bankAccountInfo.accountType,
              bankAccountHeldBy: values.bankAccountInfo.accountHeldBy,
              bankAccountHolder: values.bankAccountInfo.accountHolder,
              isOauth: true,
            },
          },
        },
      });

      if (data?.office?.settingsPaymentsAndTaxesSetup?.errors?.message) {
        makeToast("error", data.office.settingsPaymentsAndTaxesSetup.errors.message);
      } else if (data?.office?.settingsPaymentsAndTaxesSetup?.errors?.fields) {
        setFieldsErrors(data.office.settingsPaymentsAndTaxesSetup.errors);
      } else {
        await onUpdateStoreCurrentSetupPage("complete", true);
        onOnboardingComplete();
      }
    }
  };

  const handleFinishLater = () => {
    onOnboardingComplete();
  };

  return (
    <div css={styles.main}>
      <OAuthStepHeader currentStep={OAuthStep.PAYMENT} />
      <OAuthBankingForm
        values={values}
        profitMarginIncrements={profitMarginIncrements}
        isFullSsnRequired={isFullSsnRequired}
        isUpdating={isUpdating}
        formErrors={formErrors}
        onChange={onChange}
        onGoBack={handleGoBack}
        onFinishLater={handleFinishLater}
        onComplete={handleComplete}
      />
    </div>
  );
};

export { OAuthBankingInformation };
